/**
 * Centralised DOM identifiers used for testing and external automation.
 *
 * Two kinds of identifiers live here:
 *   - `testIds.*`         → values applied via `data-testid` attributes (RTL queries, internal tests).
 *   - `automationIds.*`   → values applied via HTML `id` attributes consumed by external automation
 *                          (Microsoft Clarity, QA suites). These strings are an external contract —
 *                          do not rename them without coordinating with the consumers.
 *
 * When you need a new test handle, add a key here first and reference the constant from JSX.
 * The ESLint rule `no-restricted-syntax` blocks inline `data-testid="..."` literals.
 */

export const testIds = {
  app: {
    routerProvider: 'router-provider',
  },
  core: {
    icon: {
      errorSvg: 'error-icon-svg',
      errorPath: 'error-icon-path',
      warningSvg: 'warning-icon-svg',
      warningPath: 'warning-icon-path',
      successSvg: 'success-icon-svg',
      successPath: 'success-icon-path',
      outlinedWarning: 'outlined_warning_icon',
    },
    modal: {
      overlay: 'overlay',
    },
    pillTag: 'pillDiv',
    sortToggle: {
      ascending: 'ascendingSortButton',
      descending: 'descendingSortButton',
      defaultAscending: 'defaultAscendingSortButton',
      defaultDescending: 'defaultDescendingSortButton',
    },
    maintenanceBanner: 'banner-el',
    drawerMenuItem: {
      shieldCenter: 'listmenu-shield-center',
    },
    header: {
      changelogIndicator: 'changelog-indicator',
      dropdown: 'dropdown',
    },
    menuWithDropdown: 'menu-with-dropdown',
    googleMap: {
      provider: 'provider',
      map: 'map',
    },
  },
} as const;

/**
 * HTML `id` attributes that form an external automation contract with
 * Microsoft Clarity and QA test suites. Renaming or removing any of these
 * values will break external automation — coordinate with QA/Clarity first.
 */
export const automationIds = {
  /** Husqvarna Uptime Center logo. */
  utcLogo: 'utc_logo',
  /** Header info/notification icon container. */
  utcInfoIcon: 'utcInfoIcon',
  /** Header avatar / settings menu. */
  utcAvatar: 'utcAvatar',
  /** Dashboard card shown when the user has no active access. */
  utcNotActive: 'utc_not_active',
  /** Search "no results found" title. */
  notFoundResultTitle: 'nrf_title',
  /** Search "no results found" description. */
  notFoundResultDescription: 'nrf_desc',
  warningText: 'warning_text',
} as const;
